// P2 — Painel de indicadores agregados (REL-CMP-001). Filtro de período + export
// CSV. Consome GET /api/comite/indicadores (só contagens, zero decifragem/PII).
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, categoriaLabel } from '@aions/ui-primitives';
import type { IndicadoresReport } from '@aions/core';
import { apiFetch } from '../lib/auth-client';

// ⚠️ Esta tela mantinha uma cópia local da lista de categorias com 11 entradas,
// e ela alimentava a TABELA e o CSV. O modo de falha era pior que um rótulo
// errado: a 12ª categoria (`violacao_conduta_etica`) simplesmente NÃO GERAVA
// LINHA — sumia do relatório que vai para a auditoria, e a distribuição deixava
// de somar `recebidas` sem nenhum sinal de erro. A lista agora vem do core, que
// é a mesma fonte do CHECK da migration; o rótulo vem do i18n.
import { DENUNCIA_CATEGORIAS } from '@aions/core';

const isoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** `catLabel` é injetado para que o CSV use o MESMO rótulo da tela (i18n). */
function toCsv(r: IndicadoresReport, catLabel: (slug: string) => string): string {
  const rows: Array<Array<string | number>> = [
    ['Indicador', 'Valor'],
    ['Período de', isoDate(r.periodo.deEpochMs)],
    ['Período até', isoDate(r.periodo.ateEpochMs)],
    ['1. Recebidas', r.recebidas],
    ['2. Procedentes', r.procedentes],
    ['3. Improcedentes', r.improcedentes.improcedente],
    ['3. Improcedentes por insuficiência', r.improcedentes.improcedente_insuficiencia],
    ['3. Improcedentes (total)', r.improcedentes.total],
    ['4. Arquivadas na triagem', r.arquivadasNaTriagem],
    ['5. Em andamento', r.emAndamento],
    ['6. Tempo médio (dias)', r.tempoMedioDias ?? '—'],
    ['7. Com retorno ao denunciante', r.comRetornoAoDenunciante],
    ['8. Relatos de retaliação', r.relatosRetaliacao],
    ['', ''],
    ['Categoria', 'Quantidade'],
    ...DENUNCIA_CATEGORIAS.map((c) => [catLabel(c), r.distribuicaoPorCategoria[c] ?? 0]),
  ];
  return rows.map((row) => row.join(';')).join('\r\n');
}

function downloadCsv(r: IndicadoresReport, catLabel: (slug: string) => string): void {
  const blob = new Blob([`﻿${toCsv(r, catLabel)}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `indicadores_${isoDate(r.periodo.deEpochMs)}_${isoDate(r.periodo.ateEpochMs)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function IndicadoresPage({ onBack }: { onBack: () => void }): JSX.Element {
  const { t } = useTranslation();
  const catLabel = (slug: string): string => categoriaLabel(t, slug);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [report, setReport] = useState<IndicadoresReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (dDe: string, dAte: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const qs = dDe && dAte ? `?de=${dDe}&ate=${dAte}` : '';
      const resp = await apiFetch(`/api/comite/indicadores${qs}`);
      if (!resp.ok) {
        const b = (await resp.json().catch(() => ({}))) as { error?: string };
        setError(
          b.error === 'invalid_period'
            ? 'Período inválido.'
            : `Falha ao carregar (${resp.status}).`,
        );
        return;
      }
      const data = (await resp.json()) as IndicadoresReport;
      setReport(data);
      setDe(isoDate(data.periodo.deEpochMs));
      setAte(isoDate(data.periodo.ateEpochMs));
    } catch {
      setError('Falha de rede.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load('', ''); // default = trimestre corrente (servidor)
  }, [load]);

  const stat = (label: string, value: string | number, nota?: string): JSX.Element => (
    <div
      style={{
        padding: 12,
        border: '1px solid var(--border, #2a2a3a)',
        borderRadius: 8,
        minWidth: 150,
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
      {nota && <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>{nota}</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: 960, margin: '24px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="indicadores-back">
          ← Voltar
        </Button>
        <h2 style={{ margin: 0 }}>Indicadores (REL-CMP-001)</h2>
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
            De
            <input
              type="date"
              value={de}
              onChange={(e) => setDe(e.target.value)}
              data-testid="indicadores-de"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
            Até
            <input
              type="date"
              value={ate}
              onChange={(e) => setAte(e.target.value)}
              data-testid="indicadores-ate"
            />
          </label>
          <Button
            size="sm"
            onClick={() => void load(de, ate)}
            loading={loading}
            data-testid="indicadores-aplicar"
          >
            Aplicar período
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => report && downloadCsv(report, catLabel)}
            disabled={!report}
            data-testid="indicadores-export"
          >
            Exportar CSV
          </Button>
        </div>
        <p style={{ fontSize: 12, opacity: 0.65, marginTop: 8 }}>
          100% agregado e anonimizado — só contagens, sem identificar denunciante ou conteúdo.
        </p>
      </Card>

      {error && (
        <Alert variant="error" style={{ marginTop: 16 }}>
          {error}
        </Alert>
      )}

      {report && (
        <>
          <p style={{ opacity: 0.75, marginTop: 16 }}>
            Período: <strong>{isoDate(report.periodo.deEpochMs)}</strong> a{' '}
            <strong>{isoDate(report.periodo.ateEpochMs)}</strong>
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            {stat('1. Recebidas', report.recebidas)}
            {stat('2. Procedentes', report.procedentes)}
            {stat(
              '3. Improcedentes',
              report.improcedentes.total,
              `${report.improcedentes.improcedente} + ${report.improcedentes.improcedente_insuficiencia} (insuf.)`,
            )}
            {stat(
              '4. Arquivadas na triagem',
              report.arquivadasNaTriagem,
              'encerradas sem abrir apuração',
            )}
            {stat('5. Em andamento', report.emAndamento)}
            {stat('6. Tempo médio (dias)', report.tempoMedioDias ?? '—')}
            {stat('7. Com retorno ao denunciante', report.comRetornoAoDenunciante)}
            {stat('8. Relatos de retaliação', report.relatosRetaliacao, 'marcados na triagem')}
          </div>

          <Card style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0 }}>Distribuição por categoria</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', opacity: 0.7 }}>
                  <th style={{ padding: '4px 8px' }}>Categoria</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {DENUNCIA_CATEGORIAS.map((cat) => (
                  <tr key={cat} style={{ borderTop: '1px solid var(--border, #2a2a3a)' }}>
                    <td style={{ padding: '4px 8px' }}>{catLabel(cat)}</td>
                    <td
                      style={{
                        padding: '4px 8px',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {report.distribuicaoPorCategoria[cat] ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {report.encerradasSemDesfechoNoEstado > 0 && (
            <Alert variant="warning" style={{ marginTop: 16 }}>
              {report.encerradasSemDesfechoNoEstado} caso(s) totalmente encerrado(s) não retêm o
              desfecho no estado — não contam em procedentes/improcedentes (ver notas).
            </Alert>
          )}

          <details style={{ marginTop: 16, fontSize: 13, opacity: 0.8 }}>
            <summary>Notas metodológicas</summary>
            <ul>
              {Object.entries(report.notas).map(([k, v]) => (
                <li key={k}>
                  <strong>{k}:</strong> {v}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </div>
  );
}
